import { Component, OnDestroy, OnInit } from '@angular/core';
import { UsageReportService } from '../services/usage-report.service';
import { AuthService } from '../../../auth/auth.service';
import { Subscription } from 'rxjs/Subscription';
import { Title } from '@angular/platform-browser';
import { Page } from '../../../model/page';
import * as _ from 'lodash';
import { AppPartUsageTimeSeriesComponent } from '../../../components/app-part-usage-time-series/app-part-usage-time-series.component';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { DisplayFieldPipe } from '../../pipes/displayField.pipe';
import { ThresholdModalComponent } from 'app/components/threshold-modal/threshold-modal.component';

@Component({
  templateUrl: './usage-stats.component.html',
  styleUrls: ['./usage-stats.component.scss'],
  providers: [AuthService, UsageReportService],
})
export class UsageStatsComponent implements OnInit, OnDestroy {
  public data: any;
  public displayReports: any;
  public cols: Array<any>;

  public colsDrill0: Array<any>;
  public colsDrill1: Array<any>;
  public colsDrill2: Array<any>;

  private selectedItem: Array<any>;
  public filters: { [key: string]: any };
  private subscription: Subscription;
  public page: Page;

  public loading: boolean;
  public exportLoading: boolean;
  public reportLoaded: boolean;
  public isWorkspaceSelected: boolean;

  public windowSizeOptions = this.usageReportService.getWindowSizeOptions();
  public reportCriteriaOptions =
    this.usageReportService.getReportCriteriaOptions();
  public dataSourceOptions = this.usageReportService.getDataSourceOptions();
  private labelHierarchy = ['Plant', 'Rack', 'Part'];
  private analysisClassDescriptions = [
    { name: 'red-icon-up', description: 'Very Fast Movement' },
    { name: 'red-icon-down', description: 'Very Slow Movement' },
    { name: 'orange-icon-up', description: 'Fast Movement' },
    { name: 'orange-icon-down', description: 'Slow Movement' },
    { name: 'green-icon-up', description: 'Normal Movement (Slight Increase)' },
    { name: 'green-icon-down', description: 'Normal Movement (Slight Decrease)' },
    { name: 'green-icon', description: 'No Change' },
    { name: 'blue-icon', description: 'Insufficient History' },
    { name: 'grey-icon', description: 'No Usage' },
  ];

  public hideTooltip = false;
  public tooltipRows: Array<any> = [];
  public thresholdValues = [];
  public thresholdLabel = '';

  constructor(
    private usageReportService: UsageReportService,
    private authService: AuthService,
    private dialog: MatDialog,
    private title: Title,
    private toast: ToastrService,
    private displayFieldPipe: DisplayFieldPipe
  ) {
    this.title.setTitle('OES - Usage Stats');
  }

  ngOnInit() {
    this.initValues();
    this.isWorkspaceSelected =
      this.authService.workspaceMasterCustomerID !== null;
    if (this.isWorkspaceSelected) {
      this.runReport();
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  initValues() {
    this.filters = {
      windowSize: UsageReportService.WINDOW_3M,
      reportCriteria: UsageReportService.REPORT_HITS,
      dataSource: UsageReportService.DATA_SOURCE_SD1,
    };
    this.data = {
      records: [],
      totalRecords: 0,
      reportCriteria: this.filters.reportCriteria,
      dataSource: this.filters.dataSource,
      reportGenerated: '',
      windowDates: {
        p1Start: '',
        p1End: '',
        p2Start: '',
        p2End: '',
      },
      masterCustomer: {
        CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION: false,
        CU0_ALLOW_INVENTORY_MANAGEMENT: false,
      },
    };
    this.displayReports = {
      label: '',
      view: this.labelHierarchy[0] + ' View',
      records: [],
      totalRecords: 0,
    };
    this.cols = [];
    this.page = new Page();
    this.page.size = 50;
    this.page.pageNumber = 0;
    this.selectedItem = [];
    this.loading = false;
    this.exportLoading = false;
    this.reportLoaded = false;

    this.setDrillColumns();

    this.usageReportService.getUserThresholdValues(null).subscribe((result) => {
      this.thresholdValues = result.data;
      this.updateThresholdLabel(
        this.thresholdValues['lowerValue'],
        this.thresholdValues['upperValue']
      );
    });
  }

  runReport() {
    this.reportLoaded = false;
    this.page.pageNumber = 0;
    this.getData();
  }

  getData(resetDrillLevel = true, id = 0) {
    this.loading = true;
    const filters = _.clone(this.filters);
    if (resetDrillLevel) {
      this.selectedItem = [];
    } else {
      filters['id'] = id;
    }
    filters['drillLevel'] = this.selectedItem.length;
    filters['tabularData'] = 1;
    filters['eagerLoad'] = 0;
    filters['perPage'] = this.page.size;
    filters['page'] = this.page.pageNumber;
    this.subscription = this.usageReportService.getReport(filters).subscribe(
      (result) => {
        this.setColumns(this.selectedItem.length);
        this.reportLoaded = true;
        const data = result.data;
        data['masterCustomer'][
          'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
        ] =
          Number(
            data['masterCustomer'][
              'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
            ]
          ) === 1;
        data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT'] =
          Number(data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT']) ===
          1;
        this.dataSourceOptions = this.usageReportService.getDataSourceOptions(
          data['masterCustomer']['CU0_ALLOW_INVENTORY_MANAGEMENT'],
          data['masterCustomer'][
            'CU0_ENABLE_ORDER_ENTRY_USAGE_DATA_SYNCHRONIZATION'
          ]
        );
        this.filters.dataSource = this.usageReportService.getDataSourceChoice(
          this.filters.dataSource,
          this.dataSourceOptions
        );
        this.data = data;
        this.tooltipRows = this.getTooltipTextArray();
        this.setDisplayReportData();
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.reportLoaded = false;
      }
    );
  }

  setColumns(drillLevel = 0) {
    if (drillLevel === 0) {
      this.cols = [...this.colsDrill0];
    } else if (drillLevel === 1) {
      this.cols = [...this.colsDrill1];
    } else if (drillLevel === 2) {
      this.cols = [...this.colsDrill2];
    }
  }

  setDrillColumns() {
    this.colsDrill0 = [
      {
        field: 'PL1_SHORT_CODE',
        header: 'Plant Code',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'left',
      },
      {
        field: 'PL1_NAME',
        header: 'Plant',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'left',
      },
      {
        field: 'p1',
        header: 'Period 1 Stat',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'right',
      },
      {
        field: 'p2',
        header: 'Period 2 Stat',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'right',
      },
      {
        field: 'diff',
        header: 'Analysis',
        visible: true,
        export: { visible: true, checked: true },
        width: 250,
        align: 'center',
      },
    ];

    this.colsDrill1 = [
      {
        field: 'RA1_SHIP_TO_ID',
        header: 'Rack ID',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'left',
      },
      {
        field: 'RA1_SHIP_TO_NAME',
        header: 'Rack Name',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'left',
      },
      {
        field: 'p1',
        header: 'Period 1 Stat',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'right',
      },
      {
        field: 'p2',
        header: 'Period 2 Stat',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'right',
      },
      {
        field: 'diff',
        header: 'Analysis',
        visible: true,
        export: { visible: true, checked: true },
        width: 250,
        align: 'center',
      },
    ];

    this.colsDrill2 = [
      {
        field: 'ITEM_ID',
        header: 'Item ID',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'left',
      },

      {
        field: 'CUSTOMER_PART_NO',
        header: 'Customer Part No',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'left',
      },
      {
        field: 'p1',
        header: 'Period 1 Stat',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'right',
      },
      {
        field: 'p2',
        header: 'Period 2 Stat',
        visible: true,
        export: { visible: true, checked: true },
        width: 150,
        align: 'right',
      },
      {
        field: 'diff',
        header: 'Analysis',
        visible: true,
        export: { visible: true, checked: true },
        width: 250,
        align: 'center',
      },
      {
        field: 'CONTRACT_BIN_ID',
        header: 'Contract Bin ID',
        visible: false,
        export: { visible: false, checked: false },
        width: 150,
        align: 'left',
      },
      {
        field: 'MD1_ITEM_DESC',
        header: 'Item Desc.',
        visible: false,
        export: { visible: false, checked: false },
        width: 150,
        align: 'left',
      },
    ];
  }

  onColumnResize(event) {
    let colField = event.element.attributes['data-field'].nodeValue;
    let delta = event.delta;

    let colArray = 'colsDrill' + this.selectedItem.length;

    let itemIndex = this[colArray].findIndex((item) => item.field === colField);

    if (itemIndex !== -1 && this[colArray][itemIndex].width + delta > 0)
      this[colArray][itemIndex].width = this[colArray][itemIndex].width + delta;
  }

  onSortFunction(event) {

    event.data.sort((data1, data2) => {
      let value1 = data1[event.field];
      let value2 = data2[event.field];
      let result = null;

      if (value1 == null && value2 != null)
          result = -1;
      else if (value1 != null && value2 == null)
          result = 1;
      else if (value1 == null && value2 == null)
          result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string')
          result = value1.localeCompare(value2);
      else
          result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;

      return (event.order * result);
    });

  }

  drillDown(id) {
    const index =
      Number(id) - Number(this.page.pageNumber) * Number(this.page.size);
    if (Number(this.displayReports.records[index].recordCount) > 0) {
      this.loading = true;
      this.selectedItem.push({
        id: this.displayReports.records[index].id,
        label: this.displayReports.records[index].label,
      });
      this.page.pageNumber = 0;
      this.getData(false, this.selectedItem[this.selectedItem.length - 1].id);
    } else {
      if (this.selectedItem.length === 2) {
        const dialogRef = this.dialog.open(AppPartUsageTimeSeriesComponent, {
          width: window.innerWidth - 100 + 'px',
          height: window.innerHeight - 80 + 'px',
          role: 'dialog',
          data: {
            MD1_ID: this.displayReports.records[index].id,
            windowSize: this.filters.windowSize,
            reportCriteria: this.filters.reportCriteria,
            dataSource: this.filters.dataSource,
          },
        });
      }
    }
  }

  drillUp() {
    if (this.selectedItem.length > 0) {
      this.loading = true;
      this.selectedItem.pop();
      this.page.pageNumber = 0;
      if (this.selectedItem.length > 0) {
        this.getData(false, this.selectedItem[this.selectedItem.length - 1].id);
      } else {
        this.getData(true);
      }
    }
  }

  setDisplayReportData() {
    const data = this.data.records.slice();
    const drillLevel = this.selectedItem.length;
    let label = '';
    if (drillLevel > 0) {
      for (let tracker = 0; tracker < drillLevel; tracker++) {
        label +=
          (tracker === 0 ? '' : ' > ') +
          this.labelHierarchy[tracker] +
          ': ' +
          this.selectedItem[tracker].label;
      }
    }
    this.displayReports = {
      label: label,
      view: this.labelHierarchy[drillLevel] + ' View',
      records: data,
      totalRecords: this.data.totalRecords,
    };
  }

  onPage(event) {
    this.page.size = event.rows;
    this.page.pageNumber = event.first / event.rows;
    if (this.selectedItem.length > 0) {
      this.getData(false, this.selectedItem[this.selectedItem.length - 1].id);
    } else {
      this.getData(true);
    }
  }

  onSelectDataSource(event: MatRadioChange) {
    this.filters['dataSource'] = event.value;
    if (this.selectedItem.length > 0) {
      this.getData(false, this.selectedItem[this.selectedItem.length - 1].id);
    } else {
      this.getData(true);
    }
  }

  onExport() {
    this.exportLoading = true;
    const filters = _.clone(this.filters);
    filters['id'] =
      this.selectedItem.length > 0
        ? this.selectedItem[this.selectedItem.length - 1]['id']
        : 0;
    filters['drillLevel'] = this.selectedItem.length;
    filters['tabularData'] = 1;
    filters['eagerLoad'] = 0;
    this.usageReportService.export(filters).subscribe(
      (result) => {
        if (result.success) {
          setTimeout(() => {
            // delay to discourage hitting export multiple times
            this.exportLoading = false;
          }, 5000);
          this.toast.info(
            'Export is added to queue.' +
              ' Please check the File Exports to download the file once it is ready!',
            'Update',
            {
              timeOut: 6000,
            }
          );
        } else {
          this.exportLoading = false;
          this.toast.error('Error in exporting data.');
        }
      },
      () => {
        this.exportLoading = false;
        window.dispatchEvent(new Event('resize'));
        this.toast.error('Request timed out', 'Service Error');
      }
    );
  }

  onHover(e) {
    let aux = e.target.offsetWidth < e.target.scrollWidth;
    this.hideTooltip = !aux;
  }

  getTooltipTextArray() {
    this.tooltipRows = [];
    this.data.records.forEach((element) => {
      let newObj = JSON.parse(JSON.stringify(element));

      newObj['diff'] = this.displayFieldPipe.transform(
        newObj['icon-class'],
        'name',
        'description',
        this.analysisClassDescriptions,
        'Unknown'
      );

      this.tooltipRows.push(newObj);
    }, this);
    return this.tooltipRows;
  }

  openThresholdModal() {
    const dialogRef = this.dialog.open(ThresholdModalComponent, {
      width: '400px',
      height: '285px',
      role: 'dialog',
      data: {
        lowerValue: this.thresholdValues['lowerValue'],
        upperValue: this.thresholdValues['upperValue'],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.updateThresholdLabel(result.lowerValue, result.upperValue);
        this.thresholdValues['lowerValue'] = result.lowerValue;
        this.thresholdValues['upperValue'] = result.upperValue;
      }
    });
  }

  updateThresholdLabel(lower: number, upper: number) {
    this.thresholdLabel = lower + '% - ' + upper + '%';
  }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/oes/usage-stats/usage-stats.component.ts